import { Component, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

// Matches Vite's dynamic-import failure message (e.g. "Failed to fetch dynamically imported
// module") and the browser's own "'text/html' is not a valid JavaScript MIME type" — both fire
// when a lazy-loaded route's JS chunk filename no longer exists on the server, which happens to
// any tab left open across a deploy (chunk hashes change every build, old ones get deleted). A
// plain "Try Again" can't fix this — it just re-renders the same broken import — so this class of
// error gets one automatic reload instead, which re-fetches the current index.html/chunk manifest
// and self-heals. Guarded by sessionStorage (not state, which resets on this reload) so a build
// that's genuinely broken falls through to the normal error UI after one attempt instead of loops.
const CHUNK_LOAD_ERROR_PATTERN = /dynamically imported module|is not a valid JavaScript MIME type|Importing a module script failed/i
const RELOAD_GUARD_KEY = 'rolla_chunk_reload_attempted'

function isChunkLoadError(error: Error): boolean {
  return CHUNK_LOAD_ERROR_PATTERN.test(error.message)
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error) {
    if (isChunkLoadError(error) && !sessionStorage.getItem(RELOAD_GUARD_KEY)) {
      sessionStorage.setItem(RELOAD_GUARD_KEY, '1')
      window.location.reload()
    }
  }

  render() {
    if (!this.state.hasError) {
      // Clears on every healthy render so a later, unrelated chunk-load failure (e.g. after the
      // next real deploy) still gets its one auto-reload rather than being permanently skipped
      // for the rest of this browser session.
      sessionStorage.removeItem(RELOAD_GUARD_KEY)
    }
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback
      return (
        <div className="flex flex-col items-center justify-center py-20 text-gray-500">
          <svg className="h-12 w-12 mb-3 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <p className="text-lg font-medium text-gray-700">Something went wrong</p>
          <p className="mt-1 text-sm">{this.state.error?.message}</p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="mt-4 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            Try Again
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
